import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { AvailabilityByDayType, EventsByResourceType, EventsListType, EventTooltipType, EventType, SchedulerDraggingPlaceholder, SchedulingView } from '../SchedulingTypes';
import { Droppable } from 'services/dragdrop/Droppable';
import { generateDayKey } from '../functions/schedulingDateUtils';
import usePixelsPerMinute from './functions/usePixelsPerMinute';
import ProjectModal from '../../jobs/ProjectModal';
import EmployeeEvent from './EmployeeEvent';
import JobModal from '../../jobs/JobModal';
import { calculateRowHeight } from './functions/calculateRowHeight';
import { truncate } from 'services/utils/truncate';
import EmployeeEventTooltip from './EmployeeEventTooltip';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { formatAmount } from 'services/utils/amountFormatting';

interface ProjectSchedulerResourcesProps {
    view: SchedulingView;
    days: Date[];
    hours: string[] | undefined;
    timeSlots: string[] | undefined;
    isDragging: boolean;
    draggingPlaceholder: SchedulerDraggingPlaceholder | null;
    projectJobs: ProjectJob[];
    individualJobs: IndividualJob[];
    events: EventsListType;
    eventsByResource: EventsByResourceType;
    availabilityByDay: AvailabilityByDayType;
}

export type ProjectJob = {
    id: string | number;
    created: string;
    number: string;
    job_type: string;
    description: string | null;
    required_employees: Record<string, number>;
    project: {
        id: string | number;
        ordering: number;
        name: string | null;
        number: string;
        client_name: string | null;
        work_location_city: string | null;
        default_start_time: string | null;
        default_end_time: string | null;
        default_break_duration: string | null;
    }
}

export type IndividualJob = {
    id: string | number;
    created: string;
    number: string;
    job_type: string;
    description: string | null;
    client_name: string | null;
    work_location_city: string | null;
    target_date: string | null;
    estimated_duration: string | null;
}

const ProjectSchedulerResources: React.FC<ProjectSchedulerResourcesProps> = ({ 
    view, 
    projectJobs,
    individualJobs,
    days, 
    hours, 
    timeSlots, 
    isDragging, 
    draggingPlaceholder, 
    events, 
    eventsByResource, 
    availabilityByDay,
}) => {
    const { t } = useTranslation();
    const { activeFeatures } = useAuthContext();
    const { userLocale } = useSettings();
    const { initializeModal } = useModal();
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const { pixelsPerMinute } = usePixelsPerMinute(calendarRef, view.viewType, days.length, timeSlots);
    const [eventTooltip, setEventTooltip] = useState<EventTooltipType>({ visible: false, x: 0, y: 0, event: null });

    // Get todays date
    const today = new Date().toLocaleDateString(userLocale, { weekday: 'short', day: 'numeric', month: 'numeric' });

    // Generate time slot class name, to show divider borders
    const generateTimeSlotClass = (
        timeSlot: string, 
        startTime: string, 
        endTime: string, 
        dragOver: boolean, 
        isDragging: boolean,
        hasEvent: boolean
    ): string => {
        // Set the base class
        let className = 'time-slot';

        // If the timeslot is the same as the view start- and end time, return base class
        if (timeSlot === startTime || timeSlot === endTime) {
            return className;
        }

        // Determine the timeslot class based on the minutes
        const minutes = timeSlot.split(':')[1];
        if (minutes === '30') className += ' half-hour';
        if (minutes === '00') className += ' whole-hour';

        // Add no hover if dragging over this timeslot
        if (dragOver || isDragging) className += ' no-hover';

        // Add classname when the timeslot has an event
        if (hasEvent) className += ' has-event';

        return className;
    };

    // Group the project jobs per project
    const projectGroupsMap = new Map<string, ProjectJob[]>();
    projectJobs.forEach(project_job => {
        const projectId = String(project_job.project.id);

        if (!projectGroupsMap.has(projectId)) {
            projectGroupsMap.set(projectId, []);
        }

        projectGroupsMap.get(projectId)!.push(project_job);
    });

    // Sort the project groups on their ordering value
    const projectGroups = Array.from(projectGroupsMap.values()).sort((a, b) => {
        return a[0].project.ordering - b[0].project.ordering;
    });

    // Handle open the project modal
    const handleOpenProjectModal = (projectId: number | null) => {
        if (!projectId) return;

        // Get the start date of the week
        const weekStart = days?.[0];

        initializeModal(
            React.cloneElement(<ProjectModal />, {
                weekStartDate: weekStart
            }), { 
                itemId: projectId,
                modalSize: 'large-without-sidetab'
            } 
        );
    }

    // Handle open the job modal
    const handleOpenJobModal = (jobId: number | null) => {
        initializeModal(
            React.cloneElement(<JobModal />), { 
                itemId: jobId, 
                modalSize: 'large' 
            }
        );
    };

    // Set the dragging placeholder style
    const determineDraggingPlaceholderStyle = (type: 'timeSlot' | 'list', durationMinutes?: number, pixelsPerMinute?: number): React.CSSProperties | undefined => {

        // Set the size of the event
        let eventSize;
        if (pixelsPerMinute && durationMinutes) {
            eventSize = durationMinutes * pixelsPerMinute;
        
        // For scheduling list view, set a hardcoded event size
        } else {
            eventSize = 1.75;
        }
    
        // The events are positioned from left to right
        return {
            position: type === 'timeSlot' ? 'absolute' : 'relative',
            top: '0px',
            left: `0px`,
            width: type === 'timeSlot' ? `${eventSize}px` : undefined,
            height: type === 'timeSlot' ? '99%' : `${eventSize}rem`,
            marginTop: type === 'timeSlot' ? undefined : '0.25rem',
            marginLeft: type === 'timeSlot' ? undefined : '0.25rem',
            marginRight: type === 'timeSlot' ? undefined : '0.25rem',
            backgroundColor: '#ffd1a3',
            border: '1px dashed #df4203',
            borderRadius: '0.25rem',
            zIndex: 1000
        };
    };

    // Show the event tooltip
    const showTooltip = (x: number, y: number, event: EventType) => {
        setEventTooltip({ visible: true, x, y, event });
    };

    // Hide the event tooltip
    const hideTooltip = () => {
        setEventTooltip(prev => ({ ...prev, visible: false }));
    };

    // Strip html tags
    const stripHtml = (html?: string | null): string => {
        const doc = new DOMParser().parseFromString(html ?? '', 'text/html')
        return doc.body.textContent ?? ''
    };

    // Format the target date
    const formatDisplayDate = (date: Date, userLocale: string): string => {
        return date.toLocaleDateString(userLocale, { 
            day: 'numeric',
            month: 'short'
        });
    }
    
    return (
        <div ref={calendarRef} 
             className='calendar old-style'>

            <div className='sticky-header'>

                {/* Day names */}
                <div className={`header grid-${days.length}-columns`}>
                    <div className='name-column'></div>
                    {days.map((day, index) => {
                        return (
                            <div className='day-column' 
                                key={index}>
                                <div className='date-name'>
                                    <span className={day.toLocaleDateString(userLocale, { weekday: 'short', day: 'numeric', month: 'numeric' }) === today ? 'today' : ''}>
                                        {day.toLocaleDateString(userLocale, { weekday: 'short', day: 'numeric', month: 'numeric' }) === today ? (
                                            t('date_time.relative_days.today')
                                        ): (
                                            day.toLocaleDateString(userLocale, { weekday: 'short', day: 'numeric', month: 'numeric' })
                                        )}
                                    </span>
                                </div>
                                {hours && (
                                    <div className='header-hours'>
                                        {hours.map((hour, hourIndex) => {
                                            return (
                                                <div key={hourIndex} className={`header-hour ${hourIndex === 0 ? 'invisible' : ''}`}>
                                                    {hour}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            
                {/* Render available employees */}
                <div className="resource-group">
                    <div className='resource-group-header'>
                        {t('scheduling.scheduling_board.project_scheduler_resource_group_header')}
                    </div>
                    <div className='resources'>
                        <div className={`resource-row grid-${days.length}-columns available-employees`}
                            data-resource-id="available_employees">

                                {/* Resource name */}
                                <div className='name-column'>
                                    <div className='resource-details'>
                                        {/* <div>Beschikbare medewerkers</div> */}
                                    </div>
                                </div>

                                {/* Render days */}
                                {days.map((day) => {

                                    // Generate the day key
                                    const dayKey = generateDayKey(day);

                                    // Get the available employee events of the day
                                    const dayAvailability = availabilityByDay[dayKey] || {}

                                    // Get the employee ids
                                    const employeeIds = Object.keys(dayAvailability)

                                    // Determine if the dragging is over the current day column
                                    const dragOverCurrentDayColumn =
                                        draggingPlaceholder?.resourceId === "available_employees" &&
                                        draggingPlaceholder?.day === dayKey &&
                                        view.timeDisplayMode !== 'timeslots';

                                    return (
                                        <Droppable 
                                                droppableId={{ 
                                                    type: 'availableEmployeesRow', 
                                                    resourceId: 'available_employees',
                                                    day: dayKey 
                                                }}
                                                key={`"available_employees"-${dayKey}`}
                                                showPlaceholder={false}
                                            >
                                                <div 
                                                    className='day-column' 
                                                    key={`"available_employees"-${dayKey}`} 
                                                >
                                            
                                                    {employeeIds.map((employeeId) => {

                                                        // Get the events of the employee
                                                        const employeeEvents = dayAvailability[Number(employeeId)]

                                                        return (
                                                            <div key={`employee-${employeeId}`}
                                                                className="availability-employee-row">

                                                                {timeSlots?.map((timeSlot) => {

                                                                    // Get the events in the current timeslot
                                                                    const eventsInSlot = employeeEvents.filter(event => event.timeSlot === timeSlot)

                                                                    const hasEvent = eventsInSlot.length > 0

                                                                    // Generate the timeslot class
                                                                    const timeSlotClass = generateTimeSlotClass(
                                                                        timeSlot,
                                                                        view.showTimes!.startTime,
                                                                        view.showTimes!.endTime,
                                                                        false,
                                                                        isDragging,
                                                                        hasEvent
                                                                    )

                                                                    return (
                                                                        <Droppable
                                                                            droppableId={{
                                                                                type: 'availableEmployeesRow',
                                                                                resourceId: "available_employees",
                                                                                day: dayKey,
                                                                            }}
                                                                            key={`available_employees-${employeeId}-${dayKey}-${timeSlot}`}
                                                                            showPlaceholder={false}
                                                                        >
                                                                            <div className={timeSlotClass}>
                                                                                {eventsInSlot.map((event) => (
                                                                                    <EmployeeEvent
                                                                                        key={event.key}
                                                                                        event={event}
                                                                                        currentResource={"available_employees"}
                                                                                        currentDay={dayKey}
                                                                                        currentTimeSlot={timeSlot}
                                                                                        pixelsPerMinute={pixelsPerMinute}
                                                                                        onShowTooltip={showTooltip}
                                                                                        onHideTooltip={hideTooltip}
                                                                                    />
                                                                                ))}
                                                                            </div>
                                                                        </Droppable>
                                                                    )
                                                                })}
                                                            </div>
                                                        )
                                                    })}
                                                    {dragOverCurrentDayColumn && (
                                                        <div className="dragging-placeholder" 
                                                            style={determineDraggingPlaceholderStyle('list')}
                                                        />
                                                    )}
                                                </div>
                                        </Droppable>
                                    )
                                })}
                        </div>
                    </div>
                </div> 
                {activeFeatures.includes('projects') && projectJobs.length > 0 && (
                    // Add the project group header to the sticky header 
                    <div className="resource-group">
                        <div className='resource-group-header'>
                            {t('scheduling.scheduling_board.project_scheduler_projects_group_header')}
                        </div>
                    </div>
                )}
            </div>

            <div className='scrollable-resources'>

                {/* Render project jobs */}
                {activeFeatures.includes('projects') && projectJobs.length > 0 && (
                    <div className='resources'>
                        {projectGroups?.map((projectJobsInProject) => {

                            // Get the information of the project
                            const project = projectJobsInProject[0].project;

                            // Determine if the project exists of multiple jobs
                            const multipleJobs = projectJobsInProject.length > 1;

                            return projectJobsInProject?.map((resource, resourceIndex) => {

                                // Determine the first and the last project
                                const isFirst = resourceIndex === 0;
                                const isLast = resourceIndex === projectJobsInProject.length - 1;

                                return (
                                    <div 
                                        key={resource.id} 
                                        className={`resource-row grid-${days.length}-columns project-job-row ${isLast ? 'last-row' : ''}`}
                                        style={{ height: calculateRowHeight(resource.id, eventsByResource, events) }}
                                        data-resource-id={resource.id}
                                    >

                                        {/* Resource name */}
                                        <div className={`name-column ${isFirst ? 'project-title' : ''}`}>
                                            <div 
                                                className='resource-details'
                                                onClick={() => handleOpenProjectModal(Number(resource.project.id))}
                                            >
                                                {isFirst && (
                                                    <>
                                                        <div className="title">
                                                            {project.number}
                                                            {project.name && (
                                                                <span> - {project.name}</span>
                                                            )}
                                                        </div>

                                                        <div className='remark'>
                                                            {project.client_name && (
                                                                <span>{project.client_name}</span>
                                                            )}
                                                            {project.work_location_city && (
                                                                <span> ({project.work_location_city})</span>
                                                            )}
                                                        </div>
                                                    </>
                                                )}

                                                {multipleJobs && (
                                                    <div className="project-job-sub">
                                                        {resource.job_type && (
                                                            <span>{resource.job_type}</span>
                                                        )}
                                                        {resource.description && (
                                                            <span>- {truncate(resource.description, 30)}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    
                                        {/* Render days */}
                                        {days.map((day) => {
                                            const dayKey = generateDayKey(day);
                                            const resourceEvents = (eventsByResource[resource.id!] || [])
                                                .map(eventId => events[eventId])
                                                .filter(event => event !== undefined);
                                            const dayEvents = resourceEvents.filter(event => event.dayKey === dayKey);

                                            // Get the required employees for the day
                                            const requiredEmployees = resource.required_employees?.[dayKey] ?? 0

                                            // Determine if the dragging is over the current day list
                                            const dragOverCurrentDayList =
                                                draggingPlaceholder?.resourceId === resource.id &&
                                                draggingPlaceholder?.day === dayKey &&
                                                view.timeDisplayMode !== 'timeslots';

                                            return (
                                                <div className='day-column' 
                                                    key={`${resource.id}-${dayKey}`}>
                                                    <Droppable 
                                                        droppableId={{ 
                                                            type: 'projectJob', 
                                                            resourceId: resource.id,
                                                            project_number: resource.project.number,
                                                            default_start_time: resource.project.default_start_time,
                                                            default_end_time: resource.project.default_end_time,
                                                            default_break_duration: resource.project.default_break_duration,
                                                            day: dayKey 
                                                        }}
                                                        key={`${resource.id}-${dayKey}`}
                                                        showPlaceholder={false}
                                                    >
                                                        <div className='day-list'>
                                                            <div className="capacity-indicator">
                                                                {requiredEmployees > 0 && (
                                                                    <span>{requiredEmployees}</span>
                                                                )}
                                                            </div>

                                                            {dayEvents.map((event) => (
                                                                <EmployeeEvent
                                                                    key={event.key}
                                                                    event={event}
                                                                    currentResource={resource.id}
                                                                    currentDay={dayKey}
                                                                    onShowTooltip={showTooltip}
                                                                    onHideTooltip={hideTooltip}
                                                                />
                                                            ))}
                                                            {dragOverCurrentDayList && (
                                                                <div className="dragging-placeholder" 
                                                                    style={determineDraggingPlaceholderStyle('list')}
                                                                />
                                                            )}
                                                        </div>
                                                    </Droppable>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )
                            })    
                        })}
                    </div>
                )}

                {/* Render single jobs */}
                {individualJobs.length > 0 && (
                    <div className="resource-group">
                        <div className='resource-group-header'>
                            {activeFeatures.includes('projects') ? (
                                t('scheduling.scheduling_board.project_scheduler_individual_jobs_group_header')
                            ) : (
                                t('scheduling.scheduling_board.project_scheduler_jobs_group_header')
                            )}
                        </div>
                        <div className='resources'>
                            {individualJobs.map((resource, resourceIndex) => {

                                // Strip the html tags from the description
                                const description = stripHtml(resource.description)?.trim()

                                return (
                                    <div key={resourceIndex} 
                                        className={`resource-row grid-${days.length}-columns individual-job-row`}
                                        style={{ height: calculateRowHeight(resource.id, eventsByResource, events) }}
                                        data-resource-id={resource.id}>
                                
                                        <div className='name-column'>
                                            <div 
                                                className='resource-details'
                                                onClick={() => handleOpenJobModal(Number(resource.id))}
                                            >
                                                <div className="title">
                                                    {resource.number}
                                                    {resource.job_type && (
                                                        <span> {resource.job_type}</span>
                                                    )}
                                                    {description && (
                                                        <span> - {truncate(description, 25)}</span>
                                                    )}
                                                </div>

                                                <div className='remark'>
                                                    {resource.client_name && (
                                                        <span>{resource.client_name}</span>
                                                    )}
                                                    {resource.work_location_city && (
                                                        <span> ({resource.work_location_city})</span>
                                                    )}
                                                </div>

                                                <div className='remark'>
                                                    {resource.target_date && (
                                                        <span>{t('scheduling.scheduling_board.target_date_label')} {formatDisplayDate(new Date(resource.target_date), userLocale)}</span>
                                                    )}
                                                    {resource.target_date && resource.estimated_duration && (
                                                        <span> - </span>
                                                    )}
                                                    {resource.estimated_duration && (
                                                        <span>{formatAmount(Number(resource.estimated_duration), userLocale)} {t('date_time.general.hour.singular')}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    
                                        
                                        {days.map((day) => {
                                            const dayKey = generateDayKey(day);
                                            const resourceEvents = (eventsByResource[resource.id!] || [])
                                                .map(eventId => events[eventId])
                                                .filter(event => event !== undefined);

                                            const dayEvents = resourceEvents.filter(event => event.dayKey === dayKey);

                                            return (
                                                <div className='day-column' 
                                                    key={`${resource.id}-${dayKey}`}
                                                >
                                                    {timeSlots && timeSlots.map((timeSlot) => {
                                                        // Determine if the dragging is over the current timeslot
                                                        const dragOverCurrentTimeSlot =
                                                            draggingPlaceholder?.resourceId === String(resource.id) &&
                                                            draggingPlaceholder?.day === dayKey &&
                                                            draggingPlaceholder?.timeSlot === timeSlot;

                                                        // Determine if an event is rendered within the timeslot
                                                        const hasEvent = dayEvents.some(event => event.timeSlot === timeSlot);

                                                        // Generate timeslot class name, to show divider borders
                                                        const timeSlotClass = generateTimeSlotClass(timeSlot, view.showTimes!.startTime, view.showTimes!.endTime, dragOverCurrentTimeSlot, isDragging, hasEvent);

                                                        return (
                                                            <Droppable 
                                                                droppableId={{ 
                                                                    type: 'individualJob', 
                                                                    resourceId: resource.id, 
                                                                    job_number: resource.number,
                                                                    day: dayKey, 
                                                                    timeSlot
                                                                }} 
                                                                key={`${resource.id}-${dayKey}-${timeSlot}`}
                                                                showPlaceholder={false}
                                                            >
                                                                <div className={`${timeSlotClass}`}>

                                                                    {dragOverCurrentTimeSlot && (
                                                                        <div className='tooltip'
                                                                            style={dragOverCurrentTimeSlot ? { visibility: 'visible' } : { visibility: 'hidden' }}>
                                                                            {timeSlot}
                                                                        </div>
                                                                    )}

                                                                    {dragOverCurrentTimeSlot && (
                                                                        <div className="dragging-placeholder" 
                                                                            style={determineDraggingPlaceholderStyle(
                                                                                'timeSlot',
                                                                                draggingPlaceholder.durationMinutes,
                                                                                pixelsPerMinute!,
                                                                            )}>
                                                                        </div>
                                                                    )}

                                                                    {dayEvents.filter(event => event.timeSlot === timeSlot).map((event) => (
                                                                        <EmployeeEvent
                                                                            key={event.key}
                                                                            event={event}
                                                                            currentResource={resource.id!}
                                                                            currentDay={dayKey}
                                                                            currentTimeSlot={timeSlot}
                                                                            pixelsPerMinute={pixelsPerMinute}
                                                                            onShowTooltip={showTooltip}
                                                                            onHideTooltip={hideTooltip}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </Droppable>
                                                        )
                                                    })}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

            {eventTooltip.visible && eventTooltip.event && (
                <EmployeeEventTooltip
                    event={eventTooltip.event}
                    x={eventTooltip.x}
                    y={eventTooltip.y}
                />
            )}
        </div>
    )
}

export default ProjectSchedulerResources;